import axios from '@/configs/axios'
import type { UserClientDto } from '@/models/client'
import type { PaginationDTO } from '@/models/table'
import type { IUser } from '@/models/user'

const apiUrl = process.env.VUE_APP_API_URL

export const getMyClients = (
  page = 1,
  pageSize = 20,
  search?: string,
  signal?: AbortSignal,
) => {
  const params: Record<string, any> = { page, pageSize }
  if (search) params.search = search
  return axios.get<PaginationDTO<UserClientDto>>(`${apiUrl}/user-clients/my-clients`, {
    params,
    signal,
  })
}

export const getCurrentClient = () => {
  return axios.get<UserClientDto | null>(`${apiUrl}/user-clients/current`)
}

export const switchClient = (clientId: string) => {
  return axios.post<{ clientId: string }>(`${apiUrl}/user-clients/switch/${clientId}`)
}

export const getClientsForUser = (userId: string) => {
  return axios.get<UserClientDto[]>(`${apiUrl}/user-clients/${userId}/clients`)
}

export const associateUserClient = (userId: string, clientId: string) => {
  return axios.post(`${apiUrl}/user-clients/${userId}/clients/${clientId}`)
}

export const disassociateUserClient = (userId: string, clientId: string) => {
  return axios.delete(`${apiUrl}/user-clients/${userId}/clients/${clientId}`)
}

export const getUsersByClientId = (clientId: string) =>
  axios.get<IUser[]>(`${apiUrl}/user-clients/clients/${clientId}/users`)

export const getUsersOfCurrentClient = () =>
  axios.get<IUser[]>(`${apiUrl}/user-clients/current-client/users`)

export const getUsersOfCurrentClientPaginated = (
  page: number,
  pageSize: number,
  searchString?: string,
  sortOrder?: string,
) => {
  const params: Record<string, any> = { page, pageSize }
  if (searchString) params.searchString = searchString
  if (sortOrder) params.sortOrder = sortOrder
  return axios.get(`${apiUrl}/user-clients/current-client/users/paginated`, { params })
}

export const createOrLinkUserToCurrentClient = (user: any) =>
  axios.post(`${apiUrl}/user-clients/current-client/users`, user)

export const removeUserFromCurrentClient = (userId: string) =>
  axios.delete(`${apiUrl}/user-clients/current-client/users/${userId}`)

export const associateUserToCurrentClient = (userId: string) =>
  axios.post(`${apiUrl}/user-clients/current-client/users/${userId}`)

async function bulkOperation<T>(items: T[], operation: (item: T) => Promise<any>) {
  const results = await Promise.allSettled(items.map(operation))
  const succeeded = results.filter((r) => r.status === 'fulfilled').length
  const failed = results.filter((r) => r.status === 'rejected').length
  return { succeeded, failed, total: items.length }
}

export const bulkAssociateUserClients = (userId: string, clientIds: string[]) =>
  bulkOperation(clientIds, (clientId) => associateUserClient(userId, clientId))

export const bulkDisassociateUserClients = (userId: string, clientIds: string[]) =>
  bulkOperation(clientIds, (clientId) => disassociateUserClient(userId, clientId))
